import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { del, post } from "../../lib/api";
import { timestamp } from "../../lib/format";
import type { ChargePoint } from "../../lib/types";
import { Button } from "../ui/Button";
import { ErrorNotice } from "../ui/Feedback";
import { SettingRow } from "../ui/SettingRow";
import { MicroLabel } from "../ui/Text";

/**
 * Retiring a station, and bringing a hidden one back.
 *
 * Hide what has a history, delete what has none (`lib/station.ts` → `retirement`).
 * Both are two clicks, like rotating a password: hiding drops a live connection
 * and refuses the next, and deleting cannot be undone.
 */

function useStationChange(chargePointId: string) {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ["charge-point", chargePointId] });
    void client.invalidateQueries({ queryKey: ["charge-points"] });
  };
}

/**
 * The banner a hidden station's page opens with. Its own element rather than a
 * pill in the status strip: a retired station looks exactly like a dead one
 * otherwise, and this is what tells the operator not to go and fix it.
 */
export function HiddenNotice({
  station,
  zone,
  canUnhide,
}: {
  readonly station: ChargePoint;
  readonly zone: string;
  readonly canUnhide: boolean;
}) {
  const changed = useStationChange(station.id);
  const unhide = useMutation({
    mutationFn: () => post<ChargePoint>(`/charge-points/${encodeURIComponent(station.id)}/unhide`),
    onSuccess: changed,
  });

  return (
    <div className="flex flex-col gap-3">
      {unhide.isError ? <ErrorNotice error={unhide.error} /> : null}
      <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-3 border-l-2 border-ink-muted bg-paper-raised px-4 py-3">
        <div className="flex max-w-prose flex-col gap-2">
          <MicroLabel>Hidden since {timestamp(station.hiddenAt, zone)}</MicroLabel>
          <p className="m-0 text-body text-ink">
            This station is retired. It is left out of the fleet list and the dashboard, is not on
            public sale, and is refused if it tries to connect. Its sessions and payments are kept.
          </p>
        </div>
        {canUnhide ? (
          <Button variant="ghost" onClick={() => unhide.mutate()} pending={unhide.isPending}>
            Unhide
          </Button>
        ) : null}
      </div>
    </div>
  );
}

const COPY = {
  hide: {
    value: "Has charged before, so it can be hidden but not deleted",
    lede: "Its sessions and payments have to be kept. Hiding retires it instead of deleting it.",
    trigger: "Hide station",
    confirm: (connected: boolean) =>
      `Hiding takes it off the fleet list, the dashboard and public sale, and refuses it if it connects.${
        connected ? " Its live connection is dropped now." : ""
      } You can unhide it later.`,
    act: "Hide it",
    keep: "Keep it in service",
  },
  delete: {
    value: "Has never charged, so it can be deleted",
    lede: "Nothing refers to it, so it can be removed outright.",
    trigger: "Delete station",
    confirm: (connected: boolean) =>
      `Deleting removes it, its password and its message log for good.${
        connected ? " Its live connection is dropped now." : ""
      } Registering the same id again starts from nothing.`,
    act: "Delete it",
    keep: "Keep it",
  },
} as const;

export function RetireStation({
  station,
  action,
}: {
  readonly station: ChargePoint;
  readonly action: "hide" | "delete";
}) {
  const navigate = useNavigate();
  const changed = useStationChange(station.id);
  const [isConfirming, setIsConfirming] = useState(false);
  const copy = COPY[action];
  const path = `/charge-points/${encodeURIComponent(station.id)}`;

  const retire = useMutation({
    mutationFn: () => (action === "delete" ? del<unknown>(path) : post<unknown>(`${path}/hide`)),
    onSuccess: () => {
      changed();
      setIsConfirming(false);
      // A deleted station has no page to stay on.
      if (action === "delete") navigate("/fleet", { replace: true });
    },
  });

  return (
    <SettingRow
      label="Retirement"
      value={copy.value}
      hint={copy.lede}
      actions={
        isConfirming ? null : (
          <Button variant="danger" onClick={() => setIsConfirming(true)}>
            {copy.trigger}
          </Button>
        )
      }
    >
      {retire.isError ? <ErrorNotice error={retire.error} /> : null}
      {isConfirming ? (
        <div className="flex flex-col gap-3 border-l-2 border-signal bg-paper-raised px-4 py-3">
          <p className="m-0 max-w-prose text-body text-ink">{copy.confirm(station.connected)}</p>
          <div className="flex items-center gap-3">
            <Button variant="danger" onClick={() => retire.mutate()} pending={retire.isPending}>
              {copy.act}
            </Button>
            <Button variant="ghost" onClick={() => setIsConfirming(false)}>
              {copy.keep}
            </Button>
          </div>
        </div>
      ) : null}
    </SettingRow>
  );
}
